import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@shared/contexts/LanguageContext";
import { getHistory, clearHistory } from "@shared/lib/history";
import axios from "axios";
import { Trash2, Menu } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { toast } from "sonner";
import PreferencesPanel from "@visitor/components/PreferencesPanel";

const DX_LOGO_URL =
  "https://cdn.art-mate.net/uploads/artmate/202502/thumbnail/20250228_160227_1YqJgAgCUS_p_300_0.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const History = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [exhibits, setExhibits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    fetchHistoryExhibits();
  }, []);

  const fetchHistoryExhibits = async () => {
    const historyIds = getHistory();

    if (historyIds.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const promises = historyIds.map((id) =>
        axios.get(`${API}/exhibits/${id}`).catch(() => null),
      );
      const responses = await Promise.all(promises);
      const validExhibits = responses
        .filter((res) => res !== null)
        .map((res) => res.data);
      setExhibits(validExhibits);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = () => {
    clearHistory();
    setExhibits([]);
    toast.success(
      t({ en: "History cleared", tc: "歷史已清除", sc: "历史已清除" }),
    );
  };

  const getLocalizedText = (exhibit, field) => {
    if (language === "tc") {
      return exhibit[`${field}Tc`] || exhibit[`${field}En`] || "";
    } else if (language === "sc") {
      return exhibit[`${field}Sc`] || exhibit[`${field}En`] || "";
    } else {
      return exhibit[`${field}En`] || exhibit[`${field}Tc`] || "";
    }
  };

  return (
    <div className="min-h-screen bg-black pb-20" data-testid="history-page">
      {/* Header - Enlarged */}
      <div className="sticky top-0 bg-black/90 backdrop-blur border-b border-white/10 z-10">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between">
          <img
            src={DX_LOGO_URL}
            alt="DX Logo"
            className="w-14 h-14 object-contain"
          />
          {/* <h1 className="text-2xl font-heading font-bold text-white">
            {t({ en: 'Browse History', tc: '瀏覽歷史', sc: '浏览历史' })}
          </h1> */}
          <Button
            onClick={() => setPreferencesOpen(true)}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10 w-12 h-12"
            data-testid="open-preferences"
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div
            className="text-center py-12 text-white/60"
            data-testid="loading-state"
          >
            Loading...
          </div>
        ) : exhibits.length === 0 ? (
          <div
            className="text-center py-12 space-y-4"
            data-testid="empty-history"
          >
            <p className="text-white/60">
              {t({
                en: "No exhibits visited yet",
                tc: "尚未參觀任何展品",
                sc: "尚未参观任何展品",
              })}
            </p>
            <p className="text-sm text-white/40">
              {t({
                en: "Scan a QR code to begin exploring",
                tc: "掃描二維碼開始探索",
                sc: "扫描二维码开始探索",
              })}
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-white/60">
                {exhibits.length}{" "}
                {t({
                  en: "exhibits visited",
                  tc: "已參觀展品",
                  sc: "已参观展品",
                })}
              </p>
              <Button
                onClick={handleClearHistory}
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300 hover:bg-white/10"
                data-testid="clear-history-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t({ en: "Clear", tc: "清除", sc: "清除" })}
              </Button>
            </div>

            <div className="space-y-3">
              {exhibits.map((exhibit) => (
                <button
                  key={exhibit.id}
                  onClick={() => navigate(`/exhibit/${exhibit.id}`)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-all text-left"
                  data-testid={`history-item-${exhibit.id}`}
                >
                  <div className="flex gap-4">
                    {exhibit.coverImageId && (
                      <div className="w-24 h-24 bg-white/10 flex-shrink-0">
                        <img
                          src={`${API}/assets/${exhibit.coverImageId}`}
                          alt={getLocalizedText(exhibit, "title")}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 py-4 pr-4">
                      <h3 className="font-medium text-lg mb-1 text-white">
                        {getLocalizedText(exhibit, "title")}
                      </h3>
                      <p className="text-sm text-white/60 line-clamp-2">
                        {getLocalizedText(exhibit, "description")}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Preferences Panel */}
      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
};

export default History;
